import { create } from 'zustand'
import { apiFetch, getToken, setToken, removeToken } from '@/lib/api'
import type { User, Profile } from '@/types'

// ---- State & Actions ----

interface AuthState {
  user: User | null
  profile: Profile | null
  isLoading: boolean
  isAuthenticated: boolean
}

interface AuthActions {
  initialize: () => () => void
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, username: string) => Promise<void>
  signOut: () => Promise<void>
  fetchProfile: () => Promise<void>
  updateProfile: (data: Partial<Profile>) => Promise<void>
  connectWallet: (address: string) => Promise<void>
}

type AuthStore = AuthState & AuthActions

// ---- Response shapes from the API ----

interface AuthResponse {
  access_token: string
  token_type: string
  user: User
  profile: Profile
}

// ---- Store ----

export const useAuthStore = create<AuthStore>((set, get) => ({
  // State
  user: null,
  profile: null,
  isLoading: true,
  isAuthenticated: false,

  // Initialize – check for an existing token and fetch profile
  initialize: () => {
    const token = getToken()
    if (token) {
      // We have a stored token – try to fetch the current user profile
      get()
        .fetchProfile()
        .then(() => {
          set({ isLoading: false })
        })
        .catch(() => {
          // Token is invalid / expired – clear it
          removeToken()
          set({ user: null, profile: null, isAuthenticated: false, isLoading: false })
        })
    } else {
      set({ isLoading: false })
    }

    // Return a no-op cleanup (no subscription to tear down)
    return () => {}
  },

  // Sign in with email + password
  signIn: async (email: string, password: string) => {
    set({ isLoading: true })
    try {
      const data = await apiFetch<{ token: string; profile: Profile }>('/auth/login', {
        method: 'POST',
        body: { email, password },
      })

      setToken(data.token)
      set({
        user: { id: data.profile.id, email: data.profile.email },
        profile: data.profile,
        isAuthenticated: true,
      })
    } catch (error) {
      console.error('Sign in failed:', error)
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  // Sign up with email, password, and username
  signUp: async (email: string, password: string, username: string) => {
    set({ isLoading: true })
    try {
      const data = await apiFetch<{ token: string; profile: Profile }>('/auth/register', {
        method: 'POST',
        body: { email, password, username },
      })

      setToken(data.token)
      set({
        user: { id: data.profile.id, email: data.profile.email },
        profile: data.profile,
        isAuthenticated: true,
      })
    } catch (error) {
      console.error('Sign up failed:', error)
      throw error
    } finally {
      set({ isLoading: false })
    }
  },

  // Sign out
  signOut: async () => {
    try {
      removeToken()
      set({ user: null, profile: null, isAuthenticated: false })
    } catch (error) {
      console.error('Sign out failed:', error)
      throw error
    }
  },

  // Fetch the current user's profile
  fetchProfile: async () => {
    try {
      const data = await apiFetch<{ user: User; profile: Profile }>('/auth/me')
      set({
        user: data.user,
        profile: data.profile,
        isAuthenticated: true,
      })
    } catch (error) {
      console.error('Fetch profile failed:', error)
      throw error
    }
  },

  // Update the current user's profile
  updateProfile: async (updates: Partial<Profile>) => {
    if (!get().user) throw new Error('Not authenticated')

    try {
      const data = await apiFetch<{ profile: Profile }>('/auth/me', {
        method: 'PUT',
        body: updates,
      })
      set({ profile: data.profile })
    } catch (error) {
      console.error('Update profile failed:', error)
      throw error
    }
  },

  // Connect a TRX wallet address
  connectWallet: async (address: string) => {
    if (!get().user) throw new Error('Not authenticated')

    try {
      const data = await apiFetch<{ profile: Profile }>('/auth/me/wallet', {
        method: 'POST',
        body: { address },
      })
      set({ profile: data.profile })
    } catch (error) {
      console.error('Connect wallet failed:', error)
      throw error
    }
  },
}))
